/**
 * Utility to build RTM username as JSON string with role, name, and profilePicId
 */

function buildStableIdentityKey(prefix, ...candidates) {
  const resolvedValue = candidates
    .map((candidate) => {
      if (candidate === null || candidate === undefined) {
        return ''
      }

      return String(candidate).trim()
    })
    .find((candidate) => candidate)

  if (!resolvedValue) {
    return null
  }

  return `${prefix}:${resolvedValue.trim()}`
}

/**
 * Build RTM username JSON for mentor
 * @param {Object} mentorProfileData - Mentor profile data (can be null/undefined)
 * @param {Object} userProfileData - User profile data (fallback for profilePicId, can be null/undefined)
 * @returns {string} JSON stringified username object
 */
export function buildMentorRtmUsername(mentorProfileData = null, userProfileData = null) {
  // Handle null/undefined gracefully
  if (!mentorProfileData || typeof mentorProfileData !== 'object') {
    mentorProfileData = {}
  }
  if (userProfileData && typeof userProfileData !== 'object') {
    userProfileData = null
  }

  const name = mentorProfileData?.name || mentorProfileData?.firstName || 'Mentor'
  const profilePicId = 
    mentorProfileData?.profilePicId || 
    mentorProfileData?.profilePictureId ||
    (userProfileData?.profilePicId) ||
    (userProfileData?.profilePictureId) ||
    null
  const identityKey = buildStableIdentityKey(
    'mentor',
    mentorProfileData?.id,
    mentorProfileData?.mentorId,
    mentorProfileData?.userId,
    userProfileData?.id,
    userProfileData?.userId,
  )

  return JSON.stringify({
    role: 'mentor',
    name: name || 'Mentor', // Final fallback
    profilePicId: profilePicId || null, // Explicitly set to null if missing
    identityKey,
  })
}

/**
 * Build RTM username JSON for auditor
 * @param {Object} auditorProfileData - Auditor profile data (mentor profile, can be null/undefined)
 * @param {Object} userProfileData - User profile data (fallback for profilePicId, can be null/undefined)
 * @returns {string} JSON stringified username object
 */
export function buildAuditorRtmUsername(auditorProfileData = null, userProfileData = null) {
  // Handle null/undefined gracefully
  if (!auditorProfileData || typeof auditorProfileData !== 'object') {
    auditorProfileData = {}
  }
  if (userProfileData && typeof userProfileData !== 'object') {
    userProfileData = null
  }

  const name = auditorProfileData?.name || auditorProfileData?.firstName || 'Auditor'
  const profilePicId = 
    auditorProfileData?.profilePicId || 
    auditorProfileData?.profilePictureId ||
    (userProfileData?.profilePicId) ||
    (userProfileData?.profilePictureId) ||
    null
  const identityKey = buildStableIdentityKey(
    'auditor',
    auditorProfileData?.userId,
    auditorProfileData?.id,
    auditorProfileData?.mentorId,
    userProfileData?.id,
    userProfileData?.userId,
  )

  return JSON.stringify({
    role: 'auditor',
    name: name || 'Auditor', // Final fallback
    profilePicId: profilePicId || null, // Explicitly set to null if missing
    identityKey,
  })
}

/**
 * Build RTM username JSON for patient/endUser
 * @param {Object} patientInfo - PatientInfo object (can be null/undefined)
 * @param {Object} userProfileData - User profile data (for profilePicId, can be null/undefined)
 * @param {string} fallbackName - Fallback name if patientInfo.name is not available
 * @returns {string} JSON stringified username object
 */
export function buildPatientRtmUsername(patientInfo = null, userProfileData = null, fallbackName = 'Patient') {
  // Handle null/undefined gracefully
  if (patientInfo && typeof patientInfo !== 'object') {
    patientInfo = null
  }
  if (userProfileData && typeof userProfileData !== 'object') {
    userProfileData = null
  }

  const name = (patientInfo?.name) || fallbackName || 'Patient'
  const profilePicId = 
    (userProfileData?.profilePicId) || 
    (userProfileData?.profilePictureId) ||
    null
  const identityKey = buildStableIdentityKey(
    'patient',
    patientInfo?.id,
    patientInfo?.patientId,
    patientInfo?.userId,
    userProfileData?.id,
    userProfileData?.userId,
  )

  return JSON.stringify({
    role: 'patient',
    name: name || 'Patient', // Final fallback
    profilePicId: profilePicId || null, // Explicitly set to null if missing
    identityKey,
  })
}

/**
 * Build RTM username JSON for chamber room
 * @param {string} chamberRoomName - Name of the chamber room (can be null/undefined)
 * @returns {string} JSON stringified username object
 */
export function buildChamberRoomRtmUsername(chamberRoomName = null, stableIdentity = null) {
  const name = chamberRoomName || 'Chamber Room'
  const identityKey = buildStableIdentityKey('chamber-room', stableIdentity)
  
  return JSON.stringify({
    role: 'chamber-room',
    name: name || 'Chamber Room', // Final fallback
    profilePicId: null, // Chamber rooms don't have profile pics
    identityKey,
  })
}

/**
 * Build RTM username JSON for chamber screen / kiosk devices
 * @param {string} screenName - Human-readable label for the screen
 * @param {string} stableIdentity - Stable device/screen identity
 * @returns {string} JSON stringified username object
 */
export function buildChamberScreenRtmUsername(screenName = null, stableIdentity = null) {
  const name = screenName || 'Chamber Screen'
  const identityKey = buildStableIdentityKey('chamber-screen', stableIdentity)

  return JSON.stringify({
    role: 'screen',
    name: name || 'Chamber Screen',
    profilePicId: null,
    identityKey,
  })
}

/**
 * Determine if current user is an auditor in a chamber room
 * @param {string} currentMentorId - Current mentor's ID
 * @param {Array} mentorsAndAuditors - Array of {mentorId, role} from chamberRoom
 * @returns {boolean} True if user is an auditor
 */
export function isUserAuditor(currentMentorId, mentorsAndAuditors = []) {
  if (!currentMentorId || !Array.isArray(mentorsAndAuditors)) return false
  
  const userEntry = mentorsAndAuditors.find(
    (entry) => entry?.mentorId === currentMentorId
  )
  
  // If role is explicitly 'auditor', or if not found but we're checking as auditor
  return userEntry?.role === 'auditor' || false
}

/**
 * Parse RTM username JSON (with fallback for plain strings)
 * Always returns a safe object with defaults for missing fields
 * @param {string} username - Username string (JSON or plain, can be null/undefined)
 * @returns {Object} Parsed username object with safe defaults: {role: string | null, name: string, profilePicId: string | null}
 */
export function parseRtmUsername(username) {
  // Handle null/undefined/empty gracefully
  if (!username || typeof username !== 'string' || username.trim() === '') {
    return { 
      role: null, 
      name: 'Unknown', 
      profilePicId: null,
      identityKey: null,
    }
  }
  
  try {
    const parsed = JSON.parse(username)
    
    // Validate it's an object
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      // Not a valid object, treat as plain string
      return { 
        role: null, 
        name: username, 
        profilePicId: null,
        identityKey: null,
      }
    }
    
    // Return with safe defaults for missing fields
    return {
      role: parsed.role || null,
      name: parsed.name || 'Unknown',
      profilePicId: parsed.profilePicId || null,
      identityKey: parsed.identityKey || null,
    }
  } catch (error) {
    // Not JSON, return as plain name with safe defaults
    return { 
      role: null, 
      name: username, 
      profilePicId: null,
      identityKey: null,
    }
  }
}
